import { useState, useMemo } from "react";
import type { RangeValue } from "@/shared/ui/DatePicker/useCalendar";
import {
  useGetPLReportQuery,
  useGetBalanceSheetQuery,
  useGetCashFlowQuery,
  useGetEquityChangesQuery,
} from "../api";
import { formatDateLocal } from "../utils";

const CURRENT_YEAR = new Date().getFullYear();

function createDefaultRange(): RangeValue {
  return {
    start: new Date(CURRENT_YEAR, 0, 1),
    end: new Date(CURRENT_YEAR, 11, 31),
  };
}

interface UseFinancialReportsPageParams {
  projectId?: number | null;
}

export function useFinancialReportsPage(params?: UseFinancialReportsPageParams) {
  const projectId = params?.projectId ?? undefined;
  const [dateRange, setDateRange] = useState<RangeValue>(createDefaultRange);

  const isRangeComplete = dateRange.start !== null && dateRange.end !== null;

  const appliedPeriodDates = useMemo(() => {
    if (!dateRange.start || !dateRange.end) {
      return { start_date: "", end_date: "" };
    }
    return {
      start_date: formatDateLocal(dateRange.start),
      end_date: formatDateLocal(dateRange.end),
    };
  }, [dateRange]);

  const { start_date, end_date } = appliedPeriodDates;

  const projectFilter = projectId ? { project_id: projectId } : {};
  const skipQueries = !isRangeComplete;

  const plReportQuery = useGetPLReportQuery({ start_date, end_date, ...projectFilter }, { skip: skipQueries });
  const balanceSheetQuery = useGetBalanceSheetQuery(
    { as_of_date: end_date, period_start_date: start_date, ...projectFilter },
    { skip: skipQueries },
  );
  const cashFlowQuery = useGetCashFlowQuery({ start_date, end_date, ...projectFilter }, { skip: skipQueries });

  const equityChangesYear = dateRange.end ? dateRange.end.getFullYear() : null;
  const isEquityYearSupported = equityChangesYear !== null && equityChangesYear >= 2000 && equityChangesYear <= 2100;
  const equityChangesQuery = useGetEquityChangesQuery(
    { year: equityChangesYear ?? 0 },
    { skip: skipQueries || !isEquityYearSupported },
  );

  const isFetchingAny =
    plReportQuery.isFetching ||
    balanceSheetQuery.isFetching ||
    cashFlowQuery.isFetching ||
    equityChangesQuery.isFetching;

  return {
    dateRange,
    setDateRange,
    appliedPeriodDates,
    plReport: plReportQuery.data,
    balanceSheet: balanceSheetQuery.data,
    cashFlow: cashFlowQuery.data,
    equityChanges: equityChangesQuery.data,
    equityChangesYear,
    isEquityYearSupported,
    isLoadingPLReport: plReportQuery.isLoading,
    isLoadingBalanceSheet: balanceSheetQuery.isLoading,
    isLoadingCashFlow: cashFlowQuery.isLoading,
    isLoadingEquityChanges: equityChangesQuery.isLoading,
    isFetchingAny,
    plReportError: plReportQuery.isError,
    balanceSheetError: balanceSheetQuery.isError,
    cashFlowError: cashFlowQuery.isError,
    equityChangesError: equityChangesQuery.isError,
    refetchPLReport: plReportQuery.refetch,
    refetchBalanceSheet: balanceSheetQuery.refetch,
    refetchCashFlow: cashFlowQuery.refetch,
  };
}
